import { cloneElement, HTMLAttributes, ReactElement, MouseEvent, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { PROJECTS_REGEX } from "files/hooks/useFiles";
import ProjectPrivilege from "rwb/pages/project/types/enums/privilege";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";
import useUserPermission from "~/hooks/useUserPermission";

const { VITE_RWB_API } = import.meta.env;

interface FileDownloadProps {
  fileKey?: string;
  callToAction: ReactElement<HTMLAttributes<HTMLInputElement>>;
}

const FileDownload = ({ fileKey = "", callToAction }: FileDownloadProps) => {
  const [error, setError] = useState(false);
  const downloadEnabled = useUserPermission("create:file-browser-download");
  const fileTitle = fileKey.split("/").at(-1) ?? "";
  const projectIdMatch = fileKey.match(PROJECTS_REGEX);
  const { data: projectAccessList } = useProjectsAccessList();

  const downloadFile = async () => {
    const resp = await fetch(`${VITE_RWB_API}/file-browser/generate-download-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: fileKey }),
    });

    if (resp.ok) {
      const presignedDownloadUrl = await resp.json();
      if (presignedDownloadUrl) {
        // Create temp link to trigger download programmatically
        const anchor = document.createElement("a");
        anchor.href = presignedDownloadUrl;
        anchor.style.display = "none";
        anchor.download = fileTitle;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
    } else {
      setError(true);
    }
  };

  // Attach download click handler to call to action
  const boundCTA = cloneElement(callToAction, {
    onClick: async (e: MouseEvent<HTMLInputElement>) => {
      if (callToAction.props.onClick) {
        // Preserve original callback
        callToAction.props.onClick(e);
      }
      await downloadFile();
    },
  });

  if (!downloadEnabled) {
    return null;
  }

  if (projectIdMatch) {
    const projectId = projectIdMatch.groups?.projectId;
    const accessInfo = projectAccessList?.find(
      (project) => project.project_id === Number(projectId)
    );

    if (!accessInfo?.assigned_privileges.includes(ProjectPrivilege.DOWNLOAD_PROJECT_FILES)) {
      // Hide download CTA if it's a workbench file and current user doesn't have access to download
      return null;
    }
  }

  return (
    <>
      {boundCTA}
      <Snackbar
        open={error}
        onClose={(event, reason) => {
          if (reason !== "clickaway") {
            setError(false);
          }
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={3000}
      >
        <Alert severity="error">Unable to download file</Alert>
      </Snackbar>
    </>
  );
};

export default FileDownload;
